import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { PaymentsApi } from '../../services/api/PaymentsApi';
import { PaymentsServiceApi } from '../../services/PaymentsServiceApi';
import { useAuth } from '../../contexts/AuthContext';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { MetricCard, InsightCard } from '../../components/analytics/SharedAnalyticsComponents';

const COLORS = ['#007bff', '#28a745', '#ffc107', '#dc3545', '#17a2b8', '#6f42c1', '#20c997', '#fd7e14'];

/**
 * PaymentsAdminDashboard
 * Combines data from PaymentsApi (Hibernate data) & PaymentsServiceApi (proxy Stripe service) for admin analytics.
 * Initial endpoints leveraged:
 *  - PaymentsApi.list({ limit, status, provider })
 *  - PaymentsApi.summary({ from, to })
 *  - PaymentsServiceApi.getPaymentsSummary() (may overlap; we include if available)
 * Future expansions: per-user breakdown, subscription statuses timeline, MRR / churn metrics once backend supports.
 */
export default function PaymentsAdminDashboard() {
  const { isInitialized, user } = useAuth();
  const [filters, setFilters] = useState({ from: '', to: '', status: '', provider: '', planAlias: '', userId: '', limit: 100 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [payments, setPayments] = useState([]);
  const [summary, setSummary] = useState(null); // data service
  const [serviceSummary, setServiceSummary] = useState(null); // proxy service (Stripe)
  const [refreshIndex, setRefreshIndex] = useState(0);
  const [subscriptionLookup, setSubscriptionLookup] = useState(null);
  const [lookupUserId, setLookupUserId] = useState('');
  const [lookupStripeCustomer, setLookupStripeCustomer] = useState('');
  const [paymentIdAction, setPaymentIdAction] = useState('');
  const [actionStatus, setActionStatus] = useState(null);

  const isAdmin = !!user?.roles?.some(r => r.roleName === 'admin');

  const loadData = useCallback(async () => {
    if (!isInitialized || !isAdmin) return;
    setLoading(true); setError(null);
    try {
  const listPromise = PaymentsApi.list({ status: filters.status || undefined, provider: filters.provider || undefined, limit: filters.limit || 100 });
      const summaryPromise = PaymentsApi.summary({ from: filters.from || undefined, to: filters.to || undefined });
      let serviceSummaryPromise = null;
      try { serviceSummaryPromise = PaymentsServiceApi.getPaymentsSummary(); } catch {/* ignore sync err */}
      const [listRes, summaryRes, serviceSumm] = await Promise.all([listPromise, summaryPromise, serviceSummaryPromise]);
      if (listRes?.ok) {
        try { const json = await listRes.json(); setPayments(Array.isArray(json.items) ? json.items : (Array.isArray(json) ? json : [])); } catch { setPayments([]); }
      } else { setPayments([]); }
      if (summaryRes?.ok) {
        try { setSummary(await summaryRes.json()); } catch { setSummary(null); }
      }
      if (serviceSumm) setServiceSummary(serviceSumm); // already parsed in wrapper
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [filters, isInitialized, isAdmin]);

  useEffect(() => { loadData(); }, [loadData, refreshIndex]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilters(f => ({ ...f, [name]: name === 'limit' ? Number(value) : value }));
  };

  const clearFilters = () => setFilters({ from: '', to: '', status: '', provider: '', planAlias:'', userId:'', limit: 100 });
  const reload = () => setRefreshIndex(i => i + 1);

  // Aggregations
  const aggregates = useMemo(() => {
    if (!payments.length) return null;
    const byStatus = {};
    const byProvider = {};
    const byDay = {};
    let totalAmount = 0; let currency = 'USD';
    payments.forEach(p => {
      const status = p.status || 'unknown';
      const provider = p.provider || p.paymentProvider || 'unknown';
      const amount = p.amount != null ? p.amount : (p.totalAmount != null ? p.totalAmount : 0);
      if (p.currency) currency = p.currency.toUpperCase();
      if (!byStatus[status]) byStatus[status] = { status, count:0, amount:0 };
      byStatus[status].count += 1; byStatus[status].amount += amount;
      if (!byProvider[provider]) byProvider[provider] = { provider, count:0, amount:0 };
      byProvider[provider].count += 1; byProvider[provider].amount += amount;
      const dateKey = (p.createdAt || p.created || p.timestamp || '').slice(0,10) || 'unknown';
      if (!byDay[dateKey]) byDay[dateKey] = { date: dateKey, count:0, amount:0 };
      byDay[dateKey].count += 1; byDay[dateKey].amount += amount;
      totalAmount += amount;
    });
    return {
      totalCount: payments.length,
      totalAmount,
      currency,
      byStatus: Object.values(byStatus).sort((a,b)=>b.count-a.count),
      byProvider: Object.values(byProvider).sort((a,b)=>b.count-a.count),
      byDay: Object.values(byDay).sort((a,b)=>a.date.localeCompare(b.date))
    };
  }, [payments]);

  const fmtMoney = (cents, cur='USD') => {
    if (cents == null) return '—';
    const v = (cents/100);
    return v.toLocaleString(undefined,{ style:'currency', currency: cur });
  };

  if (!isAdmin) {
    return <div className="settings-container"><h2>Payments Analytics</h2><div>Admin access required.</div></div>;
  }

  return (
    <div className="settings-container">
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:'0.75rem', flexWrap:'wrap' }}>
        <h2 style={{ margin:0 }}>Payments Analytics</h2>
        <div style={{ display:'flex', gap:'0.5rem', flexWrap:'wrap' }}>
          <button onClick={reload} disabled={loading}>{loading ? 'Loading…' : '🔄 Refresh'}</button>
          <button className="secondary" onClick={() => setFilters(f=>({ ...f, from:new Date(Date.now()-86400000).toISOString(), to:new Date().toISOString() }))}>24h</button>
          <button className="secondary" onClick={() => setFilters(f=>({ ...f, from:new Date(Date.now()-7*86400000).toISOString(), to:new Date().toISOString() }))}>7d</button>
          <button className="secondary" onClick={() => setFilters(f=>({ ...f, from:new Date(Date.now()-30*86400000).toISOString(), to:new Date().toISOString() }))}>30d</button>
          <button className="secondary" onClick={clearFilters}>Clear</button>
        </div>
      </div>

      <div style={{ marginTop:'1rem', background:'var(--card-bg)', padding:'1rem', borderRadius:'12px', boxShadow:'var(--shadow-sm)', border:'1px solid var(--border-light)' }}>
        <form onSubmit={e => { e.preventDefault(); loadData(); }} style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))', gap:'0.75rem' }}>
          <div>
            <label style={{ fontSize:'.7rem', fontWeight:600 }}>From (ISO)</label>
            <input name="from" value={filters.from} onChange={handleChange} />
          </div>
          <div>
            <label style={{ fontSize:'.7rem', fontWeight:600 }}>To (ISO)</label>
            <input name="to" value={filters.to} onChange={handleChange} />
          </div>
          <div>
            <label style={{ fontSize:'.7rem', fontWeight:600 }}>Status</label>
            <input name="status" value={filters.status} onChange={handleChange} placeholder="succeeded" />
          </div>
          <div>
            <label style={{ fontSize:'.7rem', fontWeight:600 }}>Provider</label>
            <input name="provider" value={filters.provider} onChange={handleChange} placeholder="stripe" />
          </div>
          <div>
            <label style={{ fontSize:'.7rem', fontWeight:600 }}>Plan Alias</label>
            <input name="planAlias" value={filters.planAlias} onChange={handleChange} placeholder="basic" />
          </div>
          <div>
            <label style={{ fontSize:'.7rem', fontWeight:600 }}>User ID</label>
            <input name="userId" value={filters.userId} onChange={handleChange} placeholder="uuid" />
          </div>
          <div>
            <label style={{ fontSize:'.7rem', fontWeight:600 }}>Limit</label>
            <select name="limit" value={filters.limit} onChange={handleChange}>
              {[50,100,250,500].map(v => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
          <div style={{ display:'flex', alignItems:'flex-end' }}>
            <button type="submit" style={{ width:'100%' }}>Apply</button>
          </div>
        </form>
      </div>

      {/* Quick subscription lookup & payment actions */}
      <div style={{ marginTop:'1rem', background:'var(--card-bg)', padding:'1rem', borderRadius:'12px', boxShadow:'var(--shadow-sm)', border:'1px solid var(--border-light)', display:'grid', gap:'0.75rem' }}>
        <h3 style={{ margin:'0 0 .25rem' }}>Admin Tools</h3>
        <form onSubmit={async e => { e.preventDefault(); setActionStatus(null); try { if (lookupUserId) { const sub = await PaymentsApi.getSubscription(lookupUserId); setSubscriptionLookup(await sub.json?.() || sub); setActionStatus('User subscription loaded'); } } catch(err){ setActionStatus(err.message); } }} style={{ display:'flex', gap:'0.5rem', flexWrap:'wrap' }}>
          <input placeholder="Lookup User ID" value={lookupUserId} onChange={e=>setLookupUserId(e.target.value)} style={{ flex:'1 1 180px' }} />
          <button type="submit">Get Subscription</button>
        </form>
        <form onSubmit={async e => { e.preventDefault(); setActionStatus(null); try { if (lookupStripeCustomer) { const res = await PaymentsApi.reverseLookupByStripeCustomer(lookupStripeCustomer); setActionStatus('Reverse lookup status: '+res.status); } } catch(err){ setActionStatus(err.message); } }} style={{ display:'flex', gap:'0.5rem', flexWrap:'wrap' }}>
          <input placeholder="Stripe Customer ID" value={lookupStripeCustomer} onChange={e=>setLookupStripeCustomer(e.target.value)} style={{ flex:'1 1 180px' }} />
          <button type="submit">Reverse Lookup</button>
        </form>
        <form onSubmit={async e => { e.preventDefault(); if (!paymentIdAction) return; setActionStatus(null); try { await PaymentsApi.reconcilePayment(paymentIdAction); setActionStatus('Payment reconcile requested'); } catch(err){ setActionStatus(err.message); } }} style={{ display:'flex', gap:'0.5rem', flexWrap:'wrap' }}>
          <input placeholder="Payment ID" value={paymentIdAction} onChange={e=>setPaymentIdAction(e.target.value)} style={{ flex:'1 1 140px' }} />
          <button type="submit">Reconcile</button>
          <button type="button" onClick={async ()=>{ if(!paymentIdAction) return; setActionStatus(null); try { await PaymentsApi.refundPayment(paymentIdAction); setActionStatus('Refund requested'); } catch(err){ setActionStatus(err.message); } }}>Refund</button>
          <button type="button" onClick={async ()=>{ if(!paymentIdAction) return; setActionStatus(null); try { await PaymentsApi.cancelPayment(paymentIdAction); setActionStatus('Cancel requested'); } catch(err){ setActionStatus(err.message); } }}>Cancel</button>
        </form>
        {actionStatus && <div style={{ fontSize:'.7rem', fontWeight:600, color:'#0f766e' }}>{actionStatus}</div>}
        {subscriptionLookup && (
          <div style={{ fontSize:'.65rem', background:'var(--surface-secondary)', padding:'8px 10px', borderRadius:'8px' }}>
            <strong>Subscription Lookup:</strong> Status {subscriptionLookup.status} Plan {subscriptionLookup.planAlias || subscriptionLookup.plan_alias || '—'} Period End {subscriptionLookup.currentPeriodEnd || subscriptionLookup.current_period_end || '—'}
          </div>
        )}
      </div>

      {error && <div style={{ marginTop:'1rem', color:'#c00' }}>{error.message}</div>}
      {loading && <div style={{ marginTop:'1rem' }}>Loading payments…</div>}

      {aggregates && !loading && (
        <>
          {/* Success Rate Warning */}
          {aggregates.byStatus && (
            (() => {
              const successCount = aggregates.byStatus.find(s => s.status === 'succeeded')?.count || 0;
              const failedCount = aggregates.byStatus.find(s => s.status === 'failed')?.count || 0;
              const totalCount = aggregates.totalCount || 1;
              const successRate = (successCount / totalCount) * 100;
              
              return successRate < 95 && (
                <div style={{ marginTop:'1rem' }}>
                  <InsightCard
                    type="warning"
                    title={`Success Rate: ${successRate.toFixed(1)}%`}
                    message={`${failedCount} of ${totalCount} payments failed. Review webhook health and payment processing.`}
                  />
                </div>
              );
            })()
          )}
          
          {/* Enhanced KPI Cards */}
          <div style={{ marginTop:'1rem', display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))', gap:'1rem' }}>
            <MetricCard 
              label="Total Payments" 
              value={aggregates.totalCount.toLocaleString()}
              subtitle={fmtMoney(aggregates.totalAmount, aggregates.currency)}
              sparklineData={(aggregates.byDay || []).map(d => ({ value: d.count || 0 }))}
              trend={(aggregates.byDay || []).length > 1 ? 
                ((aggregates.byDay[aggregates.byDay.length-1]?.count - aggregates.byDay[0]?.count) / (aggregates.byDay[0]?.count || 1) * 100) : 0
              }
              color="#007bff"
            />
            <MetricCard 
              label="Success Rate" 
              value={`${(((aggregates.byStatus?.find(s => s.status === 'succeeded')?.count || 0) / (aggregates.totalCount || 1)) * 100).toFixed(1)}%`}
              subtitle={`${aggregates.byStatus?.find(s => s.status === 'succeeded')?.count || 0} succeeded`}
              color="#28a745"
            />
            {summary && (
              <MetricCard 
                label="Summary Total" 
                value={(summary.totalCount || 0).toLocaleString()}
                subtitle={fmtMoney(summary.totalAmount, summary.currency?.toUpperCase?.() || aggregates.currency)}
                color="#ffc107"
              />
            )}
            {serviceSummary && (
              <MetricCard 
                label="Stripe Service" 
                value={(serviceSummary.totalCount || 0).toLocaleString()}
                subtitle={fmtMoney(serviceSummary.totalAmount, serviceSummary.currency?.toUpperCase?.() || aggregates.currency)}
                color="#17a2b8"
              />
            )}
          </div>
        </>
      )}

      {aggregates && aggregates.byDay.length > 0 && (
        <div style={{ marginTop:'1.5rem', background:'var(--card-bg)', padding:'1rem', borderRadius:'12px', boxShadow:'var(--shadow-sm)', border:'1px solid var(--border-light)' }}>
          <h3 style={{ marginTop:0 }}>Amount & Count by Day</h3>
          <div style={{ width:'100%', height:300 }}>
            <ResponsiveContainer>
              <LineChart data={aggregates.byDay}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis yAxisId="left" />
                <YAxis yAxisId="right" orientation="right" />
                <Tooltip formatter={(val,name)=> name==='amount'?fmtMoney(val,aggregates.currency):val} />
                <Legend />
                <Line yAxisId="left" type="monotone" dataKey="amount" name="Amount" stroke="#007bff" strokeWidth={2} dot={false} />
                <Line yAxisId="right" type="monotone" dataKey="count" name="Count" stroke="#28a745" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {aggregates && (
        <div style={{ marginTop:'1.5rem', display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(320px,1fr))', gap:'1.5rem' }}>
          <div style={{ background:'var(--card-bg)', padding:'1rem', borderRadius:'12px', boxShadow:'var(--shadow-sm)', border:'1px solid var(--border-light)' }}>
            <h3 style={{ marginTop:0 }}>By Status</h3>
            <div style={{ width:'100%', height:300 }}>
              <ResponsiveContainer>
                <BarChart data={aggregates.byStatus}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="status" hide={aggregates.byStatus.length > 8} />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="count" name="Count" fill="#17a2b8" />
                  <Bar dataKey="amount" name="Amount" fill="#6f42c1" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div style={{ background:'var(--card-bg)', padding:'1rem', borderRadius:'12px', boxShadow:'var(--shadow-sm)', border:'1px solid var(--border-light)' }}>
            <h3 style={{ marginTop:0 }}>By Provider (Pie)</h3>
            <div style={{ width:'100%', height:300 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={aggregates.byProvider} dataKey="count" nameKey="provider" outerRadius={110} label>
                    {aggregates.byProvider.map((p,i)=>(<Cell key={p.provider} fill={COLORS[i % COLORS.length]} />))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      <div style={{ marginTop:'2rem' }}>
        <h3 style={{ marginBottom:'0.5rem' }}>Payments (Raw)</h3>
        {loading && <div>Loading…</div>}
        {!loading && payments.length === 0 && <div className="empty-state">No payments for filters.</div>}
        {!loading && payments.length > 0 && (
          <div style={{ overflowX:'auto' }}>
            <table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Status</th>
                  <th>Provider</th>
                  <th>Amount</th>
                  <th>Currency</th>
                  <th>User</th>
                  <th>Created</th>
                  <th>Updated</th>
                </tr>
              </thead>
              <tbody>
                {payments.map(p => (
                  <tr key={p.id || p.paymentId}>
                    <td>{p.id || p.paymentId}</td>
                    <td>{p.status}</td>
                    <td>{p.provider || p.paymentProvider}</td>
                    <td>{fmtMoney(p.amount != null ? p.amount : p.totalAmount, p.currency?.toUpperCase?.() || aggregates?.currency)}</td>
                    <td>{p.currency?.toUpperCase?.() || aggregates?.currency}</td>
                    <td>{p.userId || p.customerUserId || '—'}</td>
                    <td>{p.createdAt ? new Date(p.createdAt).toLocaleString() : (p.created ? new Date(p.created).toLocaleString() : '—')}</td>
                    <td>{p.updatedAt ? new Date(p.updatedAt).toLocaleString() : (p.updated ? new Date(p.updated).toLocaleString() : '—')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
